import { searchFilterPropertyFiltersToFormValues } from "../searchFilterSlots.js";
import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import { ModalOpenButton } from "@msys/ui";
import { HelpOutlined as DecisionIcon } from "@mui/icons-material";
import { Button, ButtonProps, SvgIcon, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ComponentProps } from "react";
import {
  TemplateSearchSortingInput,
  TplApplicableFor,
} from "../../../../clients/graphqlTypes.js";
import type { TreeItemTemplatePlaceholderFragment } from "../Fragments.generated.js";
import { TemplateSelectModal } from "../../templates/quote/TemplateSelectModal.js";
import { useSetItemTemplatePlaceholderTemplateMutation } from "./TemplatePlaceholderSelectTemplateButton.generated.js";

interface Props {
  projectId: string | null;
  docId: string;
  itemId: string;
  templateTypeName: string;
  templateSearchFilterDefaults: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchFilterDefaults"],
    "__typename"
  >;
  templateSearchSortingDefaults: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchSortingDefaults"],
    "__typename"
  >;
  templateApplicableFor: TplApplicableFor;
  onCompleted?: (itemId: string) => void;
  type?: "button" | "icon";
  color?: ButtonProps["color"];
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  label?: string;
  disabled?: boolean;
  /** Pass null to render the button without a leading icon. */
  Icon?: typeof SvgIcon | null;
  refetchWizardQuestions: () => Promise<unknown>;
}

export const TemplatePlaceholderSelectTemplateButton = ({
  projectId,
  docId,
  itemId,
  templateTypeName,
  templateSearchFilterDefaults,
  templateSearchSortingDefaults,
  templateApplicableFor,
  onCompleted,
  type = "button",
  color = "secondary",
  variant = "contained",
  size = "small",
  label,
  disabled,
  Icon = DecisionIcon,
  refetchWizardQuestions,
}: Props) => {
  const { t } = useTranslate(["Decisions", "QuoteItem"]);

  const client = useApolloClient();
  const [setItemTemplatePlaceholderTemplateMutation] =
    useSetItemTemplatePlaceholderTemplateMutation({
      client,
      onCompleted: ({
        setItemTemplatePlaceholderTemplate: {
          item: { id },
        },
      }) => onCompleted?.(id),
    });

  const button =
    type === "button" ? (
      <Button
        disabled={disabled}
        color={color}
        variant={variant}
        size={size}
        startIcon={Icon ? <Icon /> : undefined}
        title={label ?? t("Question", { ns: "Decisions" })}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {label ?? t("Question", { ns: "Decisions" })}
      </Button>
    ) : type === "icon" ? (
      <Button
        disabled={disabled}
        color={color}
        variant={variant}
        size={size}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        style={{ minWidth: 32, width: 32 }}
      >
        {Icon ? (
          <Icon fontSize="small" style={{ fontSize: "1.15rem" }} />
        ) : null}
      </Button>
    ) : (
      assertNever(type)
    );

  const modalOpenButton = (
    <ModalOpenButton
      Modal={TemplateSelectModal}
      modalProps={{
        title: t("Select a {templateTypeName}", {
          ns: "Decisions",
          templateTypeName,
        }),
        initialFilters: filterDefinitionsToFormValues(
          templateSearchFilterDefaults
        ),
        fixedFilters: {
          applicableFor: [templateApplicableFor],
          templateTypeIds: templateSearchFilterDefaults.templateTypeIdFilter
            ? [templateSearchFilterDefaults.templateTypeIdFilter.valueId]
            : null,
        },
        initialSorting: sortingDefinitionsToFormValues(
          templateSearchSortingDefaults
        ),
        handleComplete: async (template, handleClose) => {
          if (template.resolvedAsReadModelVersionNumber) {
            await setItemTemplatePlaceholderTemplateMutation({
              variables: {
                input: {
                  docId,
                  itemId,
                  projectId,
                  templateQuoteId: template.id,
                  templateQuoteIdAtVersionNumber:
                    template.resolvedAsReadModelVersionNumber,
                },
              },
            });
            await refetchWizardQuestions();
            handleClose();
          }
        },
      }}
    >
      {button}
    </ModalOpenButton>
  );

  // Tooltip must wrap ModalOpenButton, not the inner Button: ModalOpenButton
  // clones its direct child to inject the open-on-click handler, and forwards
  // any extra props (like Tooltip's hover listeners + ref) on down to it.
  // Wrapping the Button directly would give the injected onClick to Tooltip
  // instead, breaking the click.
  return type === "icon" ? (
    <Tooltip title={label ?? t("Replace with template", { ns: "QuoteItem" })}>
      {modalOpenButton}
    </Tooltip>
  ) : (
    modalOpenButton
  );
};

function filterDefinitionsToFormValues(
  definitions: Omit<
    TreeItemTemplatePlaceholderFragment["templateSearchFilterDefaults"],
    "__typename"
  >
): ComponentProps<typeof TemplateSelectModal>["initialFilters"] {
  return {
    templateTypeIds: definitions.templateTypeIdFilter
      ? [definitions.templateTypeIdFilter.valueId]
      : null,
    ...searchFilterPropertyFiltersToFormValues(definitions.propertyFilters),
  };
}

function sortingDefinitionsToFormValues(
  definitions: TreeItemTemplatePlaceholderFragment["templateSearchSortingDefaults"]
): TemplateSearchSortingInput[] {
  return definitions.map(definition => ({
    propertySorting:
      definition.__typename === "EntitySearchSortingPropertyValueSorting"
        ? {
            kind: definition.kind,
            key: definition.key,
            direction: definition.direction,
          }
        : undefined,
    fieldSorting: undefined,
  }));
}
